import { Component } from '@theme/component';

/* Ingredient photography: CC0 / Public Domain via Openverse (api.openverse.org). */

/* Three.js and GSAP load once from CDN (this theme has no bundler/npm step)
   and are shared across every card instance on the page — three cards means
   one network fetch each, not three. Both are only fetched at all if the
   device already passed the WebGL2 capability check (see connectedCallback),
   so a device that can't render the card never pays for the download. */
let threePromise = null;
function loadThree() {
  if (!threePromise) threePromise = import('https://cdn.jsdelivr.net/npm/three@0.185.1/build/three.module.js');
  return threePromise;
}
let gsapPromise = null;
function loadGsap() {
  if (!gsapPromise) gsapPromise = import('https://cdn.jsdelivr.net/npm/gsap@3.15.0/index.js');
  return gsapPromise;
}

/* One WebGLRenderer + one Scene are shared across every card instance on the
   page instead of one full context per card — a WebGL context is a heavy,
   limited OS-level resource (own GPU program compile, own extension/texture
   state), so N cards used to mean N contexts for identical shader programs.
   The shared renderer draws into its own hidden canvas (never one of the
   cards' own DOM canvases); each card still owns its own visible <canvas>,
   now given a plain 2D context, and every frame blits that hidden canvas's
   pixels into its own via drawImage (see _renderFrame) — a cheap raster copy
   compared to a second GL context. This keeps every card's canvas in its
   original place in the DOM/CSS stacking order (nothing moves to a
   full-page overlay), so layout and z-index stay exactly as before. Built
   lazily on first card's init (zero cost on a page with no pyramid), and
   reference-counted via .users so the shared renderer is only disposed once
   the last card using it disconnects. */
let sharedGL = null;
function getSharedGL(THREE) {
  if (sharedGL) return sharedGL;
  const canvas = document.createElement('canvas');
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, premultipliedAlpha: true });
  } catch {
    return null;
  }
  renderer.setPixelRatio(1); // resize() already bakes DPR into the explicit width/height it passes
  renderer.setClearColor(0x000000, 0);
  const scene = new THREE.Scene();
  sharedGL = { renderer, scene, canvas, users: 0 };
  return sharedGL;
}

const TONES = {
  light: {
    bg1: [0.969, 0.953, 0.922],
    bg2: [0.867, 0.847, 0.8],
    // Ink/label match the dark tier's light-on-glass treatment (not a dark
    // ink) — now that the card face is a low-alpha glass wash instead of a
    // solid opaque fill, a dark ink loses contrast against whatever busy
    // ambient backdrop shows through; a light ink stays legible on any of
    // the three face washes plus the blurred backdrop, so all three tiers
    // now share one text-color convention instead of flipping per tone.
    ink: '#F6F2EA',
    label: '#D9CFB8',
    pulse: [0.85, 0.78, 0.42],
  },
  mid: {
    bg1: [0.867, 0.847, 0.8],
    bg2: [0.714, 0.682, 0.616],
    ink: '#F6F2EA',
    label: '#D9CFB8',
    pulse: [0.6, 0.48, 0.63],
  },
  dark: {
    bg1: [0.714, 0.682, 0.616], // continues mid.bg2 exactly, same handoff light.bg2 -> mid.bg1 already uses
    bg2: [0.4, 0.34, 0.27], // between the original [0.29, 0.235, 0.157] (too dark, broke the sequence) and
    ink: '#F6F2EA',         // the first fix [0.5, 0.45, 0.38] (too light) — still reads as the deepest tier
    label: '#D9CFB8',
    pulse: [0.56, 0.38, 0.18],
  },
};

/* Story text is composed per fragrance from its own notes rather than a
   fixed line, so every product's card reads as specific to it. "Dominant"
   notes are just the first three in the list (perfumery note lists are
   conventionally written in descending prominence) — for a tier with 1-2
   notes, all of them are used; 3+ always picks the top three, never lists
   every note, so an unusually long note list can't turn the card into an
   ingredient inventory. */
function joinNotes(names) {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function verbFor(names, singular, plural) {
  return names.length === 1 ? singular : plural;
}

/* Each tier's passage opens with a direct, factual definition sentence
   ("The top notes are...") before the sensory elaboration — restructured
   from an earlier version that opened straight into imagery. This mirrors
   a real, evidenced pattern (the 2026 GEO-SFE structural-features study)
   showing content that leads with a plain definition gets cited roughly
   2x more often by AI answer engines, independent of what it actually
   says. The general perfumery facts added (top notes fade fastest,
   heart notes form the core identity, base notes are the slowest to
   evaporate) are true of fragrance structure generally, not invented
   claims about this specific product — kept factual and defensible for
   exactly that reason. Text mirrored verbatim in snippets/fragrance-
   pyramid.liquid as real server-rendered content, not just drawn to
   canvas here — a crawler or AI agent that never runs this JS still gets
   the identical passage. */
const STORY_TEMPLATES = {
  light: (dominant) =>
    `The top notes are ${joinNotes(dominant)}. These ${verbFor(dominant, 'is', 'are')} the first impression of the fragrance, catching the light before dissolving within the first twenty minutes to reveal what waits beneath. Top notes are chosen for immediate impact rather than longevity, which is why they fade fastest of the three tiers.`,
  mid: (dominant) =>
    `The heart notes are ${joinNotes(dominant)}. Once the top notes fade, these become the fragrance's true character — the notes worn longest and felt most directly on skin. Heart notes typically emerge within the first hour and form the core identity most people associate with a fragrance, warmer and fuller than the initial impression.`,
  dark: (dominant) =>
    `The base notes are ${joinNotes(dominant)}. These are the final notes to develop, rising last and settling deep into the skin as the hours pass. Base notes are what remains after a fragrance has fully dried down, often the trace still noticeable the following morning, since these are typically the slowest-evaporating ingredients in a composition.`,
};

/* Card materials. The card is now real extruded geometry — a rounded-rect
   Shape (see roundedRectShape below) pulled into a slim slab — rather than
   a flat plane whose rounded corners were only ever a shader illusion
   (discard-based SDF masking). Real geometry means the corners stay
   perfectly circular under the card's own aspect ratio without needing an
   aspect-correction uniform, and it means there's now a genuine edge/spine
   to give its own material, not just a rim-light trick standing in for
   thickness. Front/back texture selection on the two flat cap faces still
   happens per-fragment from the transformed surface normal (Three's
   ExtrudeGeometry generates the front cap normal as +z, back cap as -z),
   so one mesh with a 2-material array — [edge, caps] — renders the whole
   object; no backface culling needed since both caps are genuinely
   single-sided, outward-facing triangles. Written in WebGL1-style GLSL
   (texture2D/gl_FragColor) since Three.js wraps that for either a WebGL1
   or WebGL2 context without extra configuration. */
const CAP_VERTEX_SHADER = `
varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vLocalNormal;
void main() {
  vUv = uv;
  vNormal = normalize(normalMatrix * normal);
  vLocalNormal = normal;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const CAP_FRAGMENT_SHADER = `
precision highp float;
varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vLocalNormal;
uniform sampler2D uTexFront;
uniform sampler2D uTexBack;
uniform vec2 uHalfSize;
uniform float uRadius;
uniform float uGeomScale;
uniform float uTime;

float sdRoundRect(vec2 p, vec2 halfSize, float r) {
  vec2 q = abs(p) - halfSize + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

void main() {
  /* Which cap a fragment belongs to is a FIXED property of the geometry
     (the front cap's local normal is always +z, the back cap's always -z)
     — it must be read from the untransformed local normal, not the one
     rotated by the mesh's current orientation. Using the transformed
     normal here was the original bug: whichever cap happens to be facing
     the camera always has a positive world-space z, by definition of
     "facing the camera", so that check could never actually detect "this
     is the back cap" — it just always resolved to front, showing the
     front texture (mirrored, since a 180°-turned object's own local +x
     reads as mirrored on screen) instead of the story text. */
  vec2 centered = (vUv - 0.5) * uHalfSize * 2.0;
  float d = sdRoundRect(centered, uHalfSize, uRadius);

  /* Water-drop lens: the cap geometry is a flat plane, so there is no real
     curvature to refract through — this fakes one entirely in the fragment
     shader. bulge rises from 0 deep in the card to 1 right at the
     silhouette, like the cross-section of a droplet's dome. It drives two
     things: a radial warp of the texture sample UV (the "magnifying lens"
     bend of whatever's printed on the card, strongest right at the edge)
     and a synthetic per-fragment normal (the flat transformed vNormal bent
     outward by the radial direction, scaled by bulge) that a fake key light
     bounces off of — that's what puts a bright, rotation-responsive
     highlight blob on the glass instead of a flat unlit tint. */
  float bulge = 1.0 - clamp(-d / (0.11 * uGeomScale), 0.0, 1.0);
  vec2 radialDir = normalize(centered + 1e-5);

  bool back = vLocalNormal.z < 0.0;
  vec2 warpedUv = vUv + radialDir * bulge * bulge * 0.026 * vec2(1.0, uHalfSize.y / uHalfSize.x);
  vec2 sampleUv = back ? vec2(1.0 - warpedUv.x, warpedUv.y) : warpedUv;
  vec4 tex = back ? texture2D(uTexBack, sampleUv) : texture2D(uTexFront, sampleUv);

  /* Chromatic aberration: real glass never bends every wavelength by the
     same amount, so a lens edge shows a faint color fringe — this is what
     reads as "glass" rather than "tinted plastic" at a glance. Only the
     red and blue channels are re-sampled at a slightly different radial
     offset (green stays as already sampled above), and the offset is
     scaled by bulge^2 so it's essentially zero at the card's center and
     only shows right at the silhouette, same footprint as the lens warp
     itself. */
  float aberration = bulge * bulge * 0.0055;
  vec2 uvR = warpedUv + radialDir * aberration * vec2(1.0, uHalfSize.y / uHalfSize.x);
  vec2 uvB = warpedUv - radialDir * aberration * vec2(1.0, uHalfSize.y / uHalfSize.x);
  vec2 sampleUvR = back ? vec2(1.0 - uvR.x, uvR.y) : uvR;
  vec2 sampleUvB = back ? vec2(1.0 - uvB.x, uvB.y) : uvB;
  tex.r = (back ? texture2D(uTexBack, sampleUvR) : texture2D(uTexFront, sampleUvR)).r;
  tex.b = (back ? texture2D(uTexBack, sampleUvB) : texture2D(uTexFront, sampleUvB)).b;

  /* A hairline champagne-gold thread traced right at the rounded
     silhouette — deliberately thin, near-invisible at a glance, with a
     slow highlight sweeping around the perimeter (angle around the shape
     center, combined with time) so it reads as light catching a real
     metal thread rather than a static painted outline. */
  float ring = 1.0 - smoothstep(0.0, 0.012 * uGeomScale, abs(d));
  float angle = atan(centered.y, centered.x);
  float shimmer = 0.72 + 0.28 * sin(angle + uTime * 0.35);
  float edgeGlow = ring * shimmer;

  vec3 fakeNormal = normalize(vNormal + vec3(radialDir * bulge * 0.55, 0.0));
  vec3 lightDir = normalize(vec3(-0.45, 0.6, 0.7));
  vec3 viewDir = vec3(0.0, 0.0, 1.0);
  vec3 halfDir = normalize(lightDir + viewDir);
  // Layered specular — a sharp hotspot, a mid halo around it, and a very
  // broad, faint ambient sheen across the whole face — is what separates a
  // "wet"-looking surface from one dot of shine; a single power term only
  // ever gives one of those at a time.
  float spec = pow(max(dot(fakeNormal, halfDir), 0.0), 60.0);
  float sheen = pow(max(dot(fakeNormal, halfDir), 0.0), 7.0) * bulge;
  float wetSheen = pow(max(dot(fakeNormal, halfDir), 0.0), 2.0);

  vec3 gold = vec3(0.804, 0.663, 0.404);
  // Snell's-window darkening: real glass/water gets noticeably MORE
  // reflective (and its own tint reads darker/more saturated) at grazing
  // angles right before the bright rim itself — a bare brightening fresnel
  // term alone reads as a glow, not an actual curved refractive edge.
  float fresnel = pow(1.0 - clamp(abs(vNormal.z), 0.0, 1.0), 1.8);
  float edgeDarken = smoothstep(0.35, 0.85, bulge) * (1.0 - smoothstep(0.85, 1.0, bulge));
  vec3 color = tex.rgb * (1.0 - edgeDarken * 0.22)
    + gold * fresnel * 0.4
    + gold * edgeGlow * 0.2
    + vec3(1.0) * wetSheen * 0.06
    + vec3(1.0) * sheen * 0.3
    + vec3(1.0) * spec * 1.0;

  /* Real glass panel: alpha comes from the texture's own alpha channel
     (opaque photo/text content, translucent tinted wash everywhere else)
     plus whatever's catching light right now — the gold rim, the droplet
     specular hotspot — so the ambient WebGL background, and through the
     canvas element's own backdrop-filter the blurred page behind it, shows
     through the card's face like true glass, not a painted card. */
  float alpha = max(tex.a, max(edgeGlow * 0.85, spec * 0.95));
  gl_FragColor = vec4(color, alpha);
}`;

/* The card's spine: unlit, with a cheap fake "light from above" gradient
   (based purely on the side wall's own outward normal, not a real scene
   light — consistent with the rest of this card system staying unlit),
   plus the same view-angle glint the caps get, so the edge reads as a
   genuine champagne-gold bevel rather than a flat colored strip. Fixed
   gold across all three tiers by design — light-tone cards need a darker
   accent than their own face to read as an edge, dark-tone cards need a
   lighter one, and one consistent metal reads as a material choice rather
   than an per-tier inconsistency. */
const EDGE_VERTEX_SHADER = `
varying vec3 vNormal;
void main() {
  vNormal = normalize(normalMatrix * normal);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const EDGE_FRAGMENT_SHADER = `
precision highp float;
varying vec3 vNormal;
uniform vec3 uEdgeColor;
uniform float uTime;

void main() {
  float up = clamp(vNormal.y * 0.5 + 0.5, 0.0, 1.0);
  float fresnel = pow(1.0 - clamp(abs(vNormal.z), 0.0, 1.0), 1.6);
  float angle = atan(vNormal.y, vNormal.x);
  float shimmer = 0.72 + 0.28 * sin(angle + uTime * 0.35);
  vec3 base = mix(uEdgeColor * 0.6, uEdgeColor * 1.25, up);
  vec3 color = base + vec3(1.0) * fresnel * 0.18 * shimmer;
  gl_FragColor = vec4(color, 1.0);
}`;

function hexToRgb01(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return [0.7, 0.7, 0.7];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
}

/* A rounded rectangle centered on the origin, built directly at the card's
   own half-width/half-height so the corner radius stays a true circle
   regardless of aspect ratio — the thing the old shader's aspect-correction
   uniform existed to fake on a plain (always-square) plane. */
function roundedRectShape(THREE, halfW, halfH, radius) {
  const r = Math.min(radius, halfW, halfH);
  const shape = new THREE.Shape();
  shape.moveTo(-halfW + r, -halfH);
  shape.lineTo(halfW - r, -halfH);
  shape.quadraticCurveTo(halfW, -halfH, halfW, -halfH + r);
  shape.lineTo(halfW, halfH - r);
  shape.quadraticCurveTo(halfW, halfH, halfW - r, halfH);
  shape.lineTo(-halfW + r, halfH);
  shape.quadraticCurveTo(-halfW, halfH, -halfW, halfH - r);
  shape.lineTo(-halfW, -halfH + r);
  shape.quadraticCurveTo(-halfW, -halfH, -halfW + r, -halfH);
  return shape;
}

const CARD_CORNER_RADIUS = 0.065; // proportioned to match the old shader-masked corner's visual size
const CARD_DEPTH = 0.045; // a slim, elegant slab — a card, not a block
const CARD_EDGE_GOLD = hexToRgb01('#CDA967'); // one champagne-gold metal for every tier's edge/rim
const GLASS_BG_ALPHA = 0.07; // face wash opacity — low enough that the blurred backdrop reads through as real glass
const TEXTURE_SCALE = 3; // rasterize text/photos at 3x the layout's own W/H so high-density phone
                          // screens don't force the GPU to upscale an already-soft texture

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

const MIN_ASPECT_MOBILE = 1.3; // mobile's on-screen width is pinned near 100vw regardless of texture size —
                               // this ratio is the only real lever for on-screen height + content legibility there
const MIN_ASPECT_DESKTOP = 3.0;

// Floor the texture size itself so a 1-2 note tier (or any future change to
// the grid math) never renders a noticeably smaller card than a full one —
// every tier reads as the same family of object regardless of note count.
const MIN_CARD_W_MOBILE = 560;
const MIN_CARD_H_MOBILE = 460;
const MIN_CARD_W_DESKTOP = 1080;
const MIN_CARD_H_DESKTOP = 460;

let measureCtx = null;
function measureTextWidth(text, fontSize) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `400 ${fontSize}px Jost, sans-serif`;
  return measureCtx.measureText(text).width;
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/* A short leftover (1-3 words) stranded alone on the final line reads as a
   mistake, not a real line break — pull words down from the line above,
   one at a time, until the last line is a real line or nothing more fits. */
function fixOrphanTail(ctx, lines, maxWidth, minWords = 4) {
  if (lines.length < 2) return lines;
  let last = lines[lines.length - 1];
  while (last.split(' ').length < minWords) {
    const prevIdx = lines.length - 2;
    const prevWords = lines[prevIdx].split(' ');
    if (prevWords.length <= 2) break; // never starve the line above into its own orphan
    const candidate = `${prevWords[prevWords.length - 1]} ${last}`;
    if (ctx.measureText(candidate).width > maxWidth) break;
    lines[prevIdx] = prevWords.slice(0, -1).join(' ');
    last = candidate;
    lines[lines.length - 1] = last;
  }
  return lines;
}

const STORY_FONT_DESKTOP = 42;
const STORY_FONT_MOBILE = 56; // bumped from 48 — flip-side story text read too small on mobile
const TIER_LABEL_FONT_DESKTOP = 32;
const TIER_LABEL_FONT_MOBILE = 48; // bumped from 40 — card title read too small on mobile
const STORY_LINE_HEIGHT_RATIO = 1.32;
const STORY_PAD = 130;   // space kept above and below the back-face story (the title sits in the top part)
const STORY_SIDE = 120;  // total left and right padding around the story text

function storyFontString(size) {
  return `italic 500 ${size}px "Cormorant Garamond", Georgia, serif`;
}


/* FIXED-CELL NOTE GRID (desktop and mobile).
   Rules from the owner:
   - One default card size. A tier with 1 to COLS notes (4 on mobile, 6 on desktop) is ONE row and is
     always exactly the default size, whatever the notes are called.
   - That row sits in the absolute centre of the glass card, horizontally and vertically.
   - More notes than COLS wrap to the next row; only then does the card grow taller.
   - The note photo and the note name are exactly the same size on every card of every product,
     so there is no shrinking with count and no font change to fit a long name: a name that does
     not fit its cell wraps onto a second line instead. */
const NOTE_GRID = {
  desktop: { cols: 6, cardW: 1842, cellW: 250, thumbR: 90, font: 34, margin: 140 },
  mobile:  { cols: 4, cardW: 1200, cellW: 262, thumbR: 96, font: 40, margin: 150 },
};

const MOBILE_GLASS_FILL = 0.84;               // share of the card the glass fills on mobile, same for every card
const DESKTOP_GLASS_FILL = 0.62 / 0.9175;     // desktop's existing look, now a constant instead of a side effect of the aspect

function wrapLabel(text, fontSize, maxW) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (measureTextWidth(test, fontSize) > maxW && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function computeGridLayout(notes, isDesktop) {
  const G = isDesktop ? NOTE_GRID.desktop : NOTE_GRID.mobile;
  const n = Math.max(notes.length, 1);
  const { thumbR, font: fontSize, cellW } = G;
  const labels = notes.map((t) => wrapLabel(t, fontSize, cellW - 4));
  const lineH = Math.round(fontSize * 1.18);
  // The owner's rule: a row holds up to G.cols notes (6 on desktop, 4 on mobile). Rows fill to that
  // limit and the remainder goes on the next row, centred. Do not "balance" the rows: that put 4
  // notes on a desktop row that should have held 6.
  const cols = Math.min(G.cols, n);
  const rows = Math.ceil(n / cols);
  const cellH = thumbR * 2 + 2 * lineH + 56;
  const labelExtent = (lines) => fontSize + 4 + (lines - 1) * lineH + fontSize * 0.25;

  const positions = notes.map((_, i) => {
    const row = Math.floor(i / cols);
    const rowStart = row * cols;
    const itemsInRow = Math.min(cols, n - rowStart);
    const col = i - rowStart;
    return { x: (col - (itemsInRow - 1) / 2) * cellW, y: (row - (rows - 1) / 2) * cellH };
  });

  // The visible block runs from the top of the first photo row to the bottom of the last label.
  // blockCenter is where that block's middle sits relative to the photo-row centre, so the caller
  // can put the block's middle exactly on the card's middle. Sizing uses a fixed two-line label
  // allowance so every default card has the same height whether its names wrap or not.
  const span = (rows - 1) * cellH;
  const lastLines = Math.max(1, ...labels.slice((rows - 1) * cols).map((l) => l.length));
  const blockCenter = (-span / 2 - thumbR + (span / 2 + thumbR + labelExtent(lastLines))) / 2;
  const blockH = span + 2 * thumbR + labelExtent(2);

  return { positions, thumbR, fontSize, labels, lineH, blockCenter, blockH, margin: G.margin, cardW: G.cardW };
}

/* Solves the full texture + DOM geometry for N notes on top of the grid
   layout above. A minimum card aspect is enforced directly rather than left
   to fall out of the content's own bounding box — with very few points
   there's no reliable "shape" to lean landscape on its own. The back face's
   editorial story has no line cap (it should read well, not be squeezed to
   fit) so the card grows taller to hold it when the note grid alone would
   leave it too short — never the reverse. */
function computeCardLayout(notes, isDesktop, toneKey) {
  const g = computeGridLayout(notes, isDesktop);

  const W = g.cardW;
  const gridH = Math.max(Math.round(g.blockH + 2 * g.margin), isDesktop ? MIN_CARD_H_DESKTOP : MIN_CARD_H_MOBILE);

  const dominant = notes.slice(0, Math.min(3, notes.length));
  const template = STORY_TEMPLATES[toneKey] || STORY_TEMPLATES.light;
  const storyText = template(dominant);
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');

  // The default card is sized tightly around the notes, so it must never be stretched to hold the
  // back-face story (that is what made the default cards far too tall, with a wide empty band above
  // and below the notes). Instead the story font is fitted DOWN, once per breakpoint, until the
  // longest story any tier can have fits inside the default height. The same fitted size is used on
  // every tier of every product, so back faces are consistent too. Only if even the smallest
  // legible size cannot fit does the card grow.
  const oneRowH = Math.max(Math.round(computeGridLayout(['x'], isDesktop).blockH + 2 * g.margin), isDesktop ? MIN_CARD_H_DESKTOP : MIN_CARD_H_MOBILE);
  const baseStory = isDesktop ? STORY_FONT_DESKTOP : STORY_FONT_MOBILE;
  const minStory = isDesktop ? 30 : 36;
  const worstNames = ['Lily of the Valley', 'Frankincense', 'Blackcurrant'];
  const storyNeed = (text, size) => {
    measureCtx.font = storyFontString(size);
    return STORY_PAD * 2 + wrapText(measureCtx, text, W - STORY_SIDE).length * Math.round(size * STORY_LINE_HEIGHT_RATIO);
  };
  let storySize = baseStory;
  const worstNeed = (size) => Math.max(...Object.values(STORY_TEMPLATES).map((t) => storyNeed(t(worstNames), size)));
  while (storySize > minStory && worstNeed(storySize) > oneRowH) storySize -= 2;

  const H = Math.max(gridH, worstNeed(storySize));
  // The notes block is centred on the card's own middle, so it stays in the absolute centre even
  // when the back-face story makes the card taller than the grid needs.
  const gridCenterY = H / 2 - g.blockCenter;
  // Desktop on-screen width tracks the texture at one constant scale (1040 px / 1842 px).
  const maxWidthPx = isDesktop ? Math.max(480, W * (1040 / 1842)) : clamp(W * 0.62, 480, 620);

  // The element (and camera frame) is the glass plus a CONSTANT margin: sideways it is the share the
  // fill fraction leaves, and top and bottom get that same physical margin at every card height. It
  // used to be a percentage of the card, so taller cards carried more empty margin and the gap
  // between two glass panels grew with card height.
  const fill = isDesktop ? DESKTOP_GLASS_FILL : MOBILE_GLASS_FILL;
  const padV = (oneRowH * (1 / fill - 1)) / 2;
  const elW = W / fill;
  const elH = H + 2 * padV;
  // Corner radius, slab thickness and the glass edge highlight are drawn in world units. A taller card
  // shows more pixels per world unit, so without this the big card had rounder corners and a thicker
  // edge than the default one. geomScale (1 for a default card, smaller for taller ones) keeps them the
  // same size on screen for every card.
  const geomScale = (oneRowH + 2 * padV) / elH;

  return { W, H, elW, elH, geomScale, thumbR: g.thumbR, fontSize: g.fontSize, positions: g.positions, maxWidthPx, storyText, storySize, labels: g.labels, lineH: g.lineH, gridCenterY };
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/* Crops a cutout canvas to the bounding box of its visible pixels, so a photo with extra
   empty margin is not drawn smaller than a tight one inside the same fixed box. */
const trimCache = new WeakMap();
function trimToContent(src) {
  if (trimCache.has(src)) return trimCache.get(src);
  let out = src;
  try {
    const w = src.width, h = src.height;
    if (w && h && typeof src.getContext === 'function') {
      const d = src.getContext('2d').getImageData(0, 0, w, h).data;
      let x0 = w, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (d[(y * w + x) * 4 + 3] > 24) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 >= x0 && y1 >= y0 && (x1 - x0 + 1 < w || y1 - y0 + 1 < h)) {
        const c = document.createElement('canvas');
        c.width = x1 - x0 + 1;
        c.height = y1 - y0 + 1;
        c.getContext('2d').drawImage(src, x0, y0, c.width, c.height, 0, 0, c.width, c.height);
        out = c;
      }
    }
  } catch { /* unreadable: keep the untrimmed cutout */ }
  trimCache.set(src, out);
  return out;
}

const bgRemovalCache = new WeakMap();
function removeWhiteBackground(img) {
  if (bgRemovalCache.has(img)) return bgRemovalCache.get(img);
  const maxDim = 480;
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  let imageData;
  try { imageData = ctx.getImageData(0, 0, w, h); }
  catch { bgRemovalCache.set(img, img); return img; }
  const px = imageData.data;

  // Library photos are pre-cut transparent PNGs now. If the border is already transparent,
  // leave the pixels alone: the white-keying below would erase white subjects (jasmine, salt).
  let clear = 0, edge = 0;
  const probe = (x, y) => { edge++; if (px[(y * w + x) * 4 + 3] < 8) clear++; };
  for (let x = 0; x < w; x++) { probe(x, 0); probe(x, h - 1); }
  for (let y = 0; y < h; y++) { probe(0, y); probe(w - 1, y); }
  if (edge && clear / edge >= 0.9) {
    bgRemovalCache.set(img, canvas);
    return canvas;
  }

  const WHITE_CUTOFF = 232;
  const FEATHER_START = 165;
  const SAT_LIMIT = 0.2;
  const lumOf = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;
  const satOf = (r, g, b) => {
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    return max === 0 ? 0 : (max - min) / max;
  };

  for (let i = 0; i < px.length; i += 4) {
    const r = px[i], g = px[i + 1], b = px[i + 2];
    const lum = lumOf(r, g, b);
    const sat = satOf(r, g, b);
    if (sat > SAT_LIMIT) continue;
    if (lum >= WHITE_CUTOFF) {
      px[i + 3] = 0;
    } else if (lum > FEATHER_START) {
      const t = (lum - FEATHER_START) / (WHITE_CUTOFF - FEATHER_START);
      px[i + 3] = Math.round(px[i + 3] * (1 - t));
    }
  }

  let rSum = 0, gSum = 0, bSum = 0, n = 0;
  const sampleBorder = (x, y) => {
    const idx = (y * w + x) * 4;
    rSum += px[idx]; gSum += px[idx + 1]; bSum += px[idx + 2]; n++;
  };
  for (let x = 0; x < w; x++) { sampleBorder(x, 0); sampleBorder(x, h - 1); }
  for (let y = 0; y < h; y++) { sampleBorder(0, y); sampleBorder(w - 1, y); }
  const refR = n ? rSum / n : 255;
  const refG = n ? gSum / n : 255;
  const refB = n ? bSum / n : 255;

  const SEED_LUM_MIN = 150;
  const TOL = 90;
  const visited = new Uint8Array(w * h);
  const stack = [];
  for (let x = 0; x < w; x++) { stack.push([x, 0]); stack.push([x, h - 1]); }
  for (let y = 0; y < h; y++) { stack.push([0, y]); stack.push([w - 1, y]); }

  while (stack.length) {
    const [x, y] = stack.pop();
    if (x < 0 || y < 0 || x >= w || y >= h) continue;
    const vIdx = y * w + x;
    if (visited[vIdx]) continue;
    visited[vIdx] = 1;
    const idx = vIdx * 4;
    const r = px[idx], g = px[idx + 1], b = px[idx + 2];
    if (px[idx + 3] === 0) {
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
      continue;
    }
    const lum = lumOf(r, g, b);
    if (lum < SEED_LUM_MIN) continue;
    const dist = Math.sqrt((r - refR) ** 2 + (g - refG) ** 2 + (b - refB) ** 2);
    if (dist > TOL) continue;
    px[idx + 3] = 0;
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }

  const erosion = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      if (px[idx + 3] === 0) continue;
      let touchesTransparent = false;
      for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        if (px[(ny * w + nx) * 4 + 3] === 0) { touchesTransparent = true; break; }
      }
      if (touchesTransparent) erosion[y * w + x] = 1;
    }
  }
  for (let i = 0; i < erosion.length; i++) {
    if (erosion[i]) px[i * 4 + 3] = 0;
  }

  ctx.putImageData(imageData, 0, 0);
  bgRemovalCache.set(img, canvas);
  return canvas;
}

/* Glassmorphism approximation (texture-level, not real transmission): a
   soft, low-alpha white wash so the face reads as glass rather than a
   painted card. The diagonal sheen streak this used to add was pulled —
   it read as a fixed reflection line baked onto every card rather than a
   material quality. Safe to revert to a flat opaque paint by removing this
   call in _buildTextures(). */
function paintGlassFrost(ctx, w, h) {
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fillRect(0, 0, w, h);
}

/**
 * A small draggable, tilting card with real thickness — an extruded rounded-rect Three.js
 * geometry (not a flat plane with a shader-faked rounded silhouette) printed with a
 * CanvasTexture on its two cap faces and a separately shaded spine on its extruded edge, plus
 * a Fresnel rim on the caps (the theme's own technique — not a third-party shader). Starts
 * collapsed and unfolds into place on scroll reveal; flipping past 90 degrees swaps to
 * a second "story" texture on the back and fires a bubbling event the ambient background
 * listens for to pulse toward that tier's color. Continuous physics (drag-follow, idle sway,
 * inertia decay) run as plain per-frame math on GSAP's ticker, since that's not what tween
 * easing is for; the two genuinely discrete, choreographed moments — the scroll-reveal unfold
 * and the grab/release squish — are real GSAP tweens driving plain numeric properties that the
 * render loop reads each frame, so the easing quality comes from GSAP itself rather than a
 * hand-rolled lerp.
 * @extends {Component}
 */
class FragrancePyramidCardComponent extends Component {
  requiredRefs = ['canvas'];

  connectedCallback() {
    super.connectedCallback();

    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._yaw = 0;
    this._pitch = 0;
    this._targetYaw = 0;
    this._targetPitch = 0;
    this._velYaw = 0;
    this._velPitch = 0;
    this._dragging = false;
    this._grabScale = 1;
    this._unfoldProgress = 0;
    this._lastX = 0;
    this._lastY = 0;
    this._start = performance.now();
    this._lastWidth = 0;
    this._lastHeight = 0;
    this._wasBackFacing = false;
    this._unfoldDelay = parseFloat(this.dataset.delay || '0') * 1000;

    this._tone = TONES[this.dataset.tone] || TONES.light;

    const noteNames = (this.dataset.notes || '').split(',').map((n) => n.trim()).filter(Boolean);
    const isDesktop = window.matchMedia('(min-width: 900px)').matches;
    this._isDesktop = isDesktop;
    this._layout = computeCardLayout(noteNames, isDesktop, this.dataset.tone);
    this.style.setProperty('--card-max-width', `${Math.round(this._layout.maxWidthPx)}px`);
    this.style.setProperty('--card-aspect', `${this._layout.elW} / ${this._layout.elH}`);

    // The back face's "story" narrative is canvas pixels — real content,
    // not decoration, and per this project's own rule (techUse.md §21)
    // that can't be canvas-only. This exists whether or not WebGL2 is
    // available below, so a no-WebGL device, a screen reader, or a
    // non-JS-executing crawler gets it without needing to drag anything.
    // The note names themselves are NOT duplicated here — they're now real
    // server-rendered text in snippets/fragrance-pyramid.liquid (a static
    // <p class="visually-hidden"> per tier), so repeating them in this
    // JS-built string would just be a redundant announcement.
    const tierLabel = this.dataset.tier || '';
    const a11yText = document.createElement('p');
    a11yText.className = 'visually-hidden';
    a11yText.textContent = [tierLabel, this._layout.storyText].filter(Boolean).join('. ');
    this.appendChild(a11yText);

    // Capability check before any network fetch (architecture.md's own
    // rule): a device without WebGL2 never downloads Three.js or GSAP —
    // the card canvas simply stays empty, same as the previous raw-WebGL
    // build's behavior when a context couldn't be created.
    const probe = document.createElement('canvas').getContext('webgl2');
    if (!probe) return;

    // Keyboard-operable flip: the drag gesture that reveals the back face
    // is pointer-only otherwise, which fails both WCAG 2.1.1 (keyboard)
    // and 2.5.7 (dragging needs a non-dragging alternative). Enter/Space
    // toggles the same _targetYaw the drag physics already animate toward,
    // so it settles with the same lerp, not an instant jump.
    this.tabIndex = 0;
    this.setAttribute('role', 'button');
    this.setAttribute('aria-pressed', 'false');
    this.setAttribute('aria-label', `${tierLabel || 'Fragrance note'} card. Press Enter to flip.`);
    this._keyboardFlipped = false;
    this.addEventListener('keydown', this._onKeyDown);

    this.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    // touch-action: pan-y (see block CSS) means a scroll gesture that
    // started on the card gets handed to native scrolling mid-touch — the
    // browser ends that pointer sequence with pointercancel, not pointerup.
    // Without this, _dragging never clears, and since pointermove listens
    // on window, that stuck card keeps reacting to every later touch
    // anywhere on the page — which is what "other cards flip too" was.
    window.addEventListener('pointercancel', this._onPointerUp);

    this._buildTextures()
      .then(() => Promise.all([loadThree(), loadGsap()]))
      .then(([THREE, { gsap }]) => {
        this._THREE = THREE;
        this._gsap = gsap;
        this._initScene();
        if (!this._renderer) return;

        this._resizeObserver = new ResizeObserver(() => this._resize());
        this._resizeObserver.observe(this);
        this._visibilityObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                if (!this._revealed) {
                  this._revealed = true;
                  this._playUnfold();
                }
                this._startLoop();
              } else {
                this._stopLoop();
              }
            }
          },
          { threshold: 0.15 }
        );
        this._visibilityObserver.observe(this);

        if (this._reducedMotion) this._unfoldProgress = 1; // set before _resize() so its own
        this._resize(); // internal reduced-motion render call already reads the settled value
        if (this._reducedMotion) {
          this._renderFrame();
        } else {
          this._startLoop();
        }
      });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopLoop();
    this._resizeObserver?.disconnect();
    this._visibilityObserver?.disconnect();
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);

    this._gsap?.killTweensOf(this);
    this._geometry?.dispose();
    this._capMaterial?.dispose();
    this._edgeMaterial?.dispose();
    this._texFront?.dispose();
    this._texBack?.dispose();

    // The renderer/scene are shared with every other card on the page —
    // only the last card to disconnect actually tears them down.
    if (this._shared) {
      this._shared.scene.remove(this._mesh);
      this._shared.users -= 1;
      if (this._shared.users <= 0) {
        this._shared.renderer.dispose();
        if (sharedGL === this._shared) sharedGL = null;
      }
    }
  }

  async _buildTextures() {
    const tierLabel = this.dataset.tier || '';
    const notes = (this.dataset.notes || '').split(',').map((n) => n.trim()).filter(Boolean);
    const images = (this.dataset.noteImages || '').split(',').map((u) => u.trim());
    const tone = this._tone;

    const loaded = await Promise.all(images.map((src) => loadImage(src)));

    const { W, H, thumbR, positions, fontSize } = this._layout;

    /* --- front: photo + name per note --- */
    // The canvas is rasterized at TEXTURE_SCALE the layout's own W/H (a
    // fixed boost, not tied to devicePixelRatio) so text and photos stay
    // sharp on high-density phone screens instead of the GPU upscaling an
    // already-soft texture — a small, fixed multiplier keeps texture size
    // bounded regardless of how large W/H already grew for a busy card.
    const front = document.createElement('canvas');
    front.width = W * TEXTURE_SCALE;
    front.height = H * TEXTURE_SCALE;
    const fx = front.getContext('2d');
    fx.scale(TEXTURE_SCALE, TEXTURE_SCALE);

    const grad = fx.createLinearGradient(0, 0, W * 0.5, H);
    grad.addColorStop(0, `rgba(${tone.bg1.map((v) => Math.round(v * 255)).join(',')},${GLASS_BG_ALPHA})`);
    grad.addColorStop(1, `rgba(${tone.bg2.map((v) => Math.round(v * 255)).join(',')},${GLASS_BG_ALPHA})`);
    fx.fillStyle = grad;
    fx.fillRect(0, 0, W, H);
    paintGlassFrost(fx, W, H);

    fx.textAlign = 'center';
    fx.fillStyle = tone.label;
    const tierLabelSize = this._isDesktop ? TIER_LABEL_FONT_DESKTOP : TIER_LABEL_FONT_MOBILE;
    fx.font = `600 ${tierLabelSize}px Jost, sans-serif`;
    let tracked = '';
    for (const ch of tierLabel.toUpperCase()) tracked += ch + '  ';
    fx.fillText(tracked.trim(), W / 2, 92);

    /* Wrapping grid: notes flow left-to-right, wrapping to a new row once a
       row fills — thumbnail radius shrinks smoothly as count rises past
       the fixed cell size, and each photo always keeps its name underneath. */
    const centerX = W / 2;
    const centerY = this._layout.gridCenterY;

    notes.forEach((note, i) => {
      const { x: dx, y: dy } = positions[i];
      const cx = centerX + dx;
      const cy = centerY + dy;
      const img = loaded[i];

      if (img) {
        // Contain-fit (not cover/crop): these are transparent-background
        // note photos, not framed portraits — the whole cutout should show,
        // not a circle punched out of it, so nothing but the note itself
        // sits on the glass.
        const cutout = trimToContent(removeWhiteBackground(img));
        const cw = cutout.width || cutout.naturalWidth;
        const ch = cutout.height || cutout.naturalHeight;
        const scale = Math.min((thumbR * 2) / cw, (thumbR * 2) / ch);
        const dw = cw * scale;
        const dh = ch * scale;
        fx.imageSmoothingEnabled = true;
        fx.imageSmoothingQuality = 'high';
        fx.drawImage(cutout, cx - dw / 2, cy - dh / 2, dw, dh);
      } else {
        const swatch = tone.pulse.map((v) => Math.round(v * 255));
        fx.fillStyle = `rgba(${swatch.join(',')},0.35)`;
        fx.beginPath();
        fx.arc(cx, cy, thumbR * 0.6, 0, Math.PI * 2);
        fx.fill();
      }

      fx.textAlign = 'center';
      fx.fillStyle = tone.ink;
      fx.font = `400 ${fontSize}px Jost, sans-serif`;
      const labelLines = (this._layout.labels && this._layout.labels[i]) || [note];
      labelLines.forEach((line, li) => {
        fx.fillText(line, cx, cy + thumbR + fontSize + 4 + li * (this._layout.lineH || 0));
      });
    });

    this._frontCanvas = front;

    /* --- back: tier "story" line --- */
    const back = document.createElement('canvas');
    back.width = W * TEXTURE_SCALE;
    back.height = H * TEXTURE_SCALE;
    const bx = back.getContext('2d');
    bx.scale(TEXTURE_SCALE, TEXTURE_SCALE);

    bx.fillStyle = grad;
    bx.fillRect(0, 0, W, H);
    paintGlassFrost(bx, W, H);

    bx.textAlign = 'center';
    bx.fillStyle = tone.label;
    bx.font = `600 ${tierLabelSize}px Jost, sans-serif`;
    bx.fillText(tracked.trim(), W / 2, 110);

    bx.fillStyle = tone.ink;
    const maxWidth = W - STORY_SIDE;
    const dominant = notes.slice(0, Math.min(3, notes.length));
    const storyText = (STORY_TEMPLATES[this.dataset.tone] || STORY_TEMPLATES.light)(dominant);
    const storySize = this._layout.storySize;
    bx.font = storyFontString(storySize);
    const lines = fixOrphanTail(bx, wrapText(bx, storyText, maxWidth), maxWidth);

    const lh = Math.round(storySize * STORY_LINE_HEIGHT_RATIO);
    const startY = H / 2 - ((lines.length - 1) * lh) / 2 + 14;
    lines.forEach((l, i) => bx.fillText(l, W / 2, startY + i * lh));

    this._backCanvas = back;
  }

  _initScene() {
    const THREE = this._THREE;
    const canvas = this.refs.canvas;

    const shared = getSharedGL(THREE);
    if (!shared) return;
    shared.users += 1;
    this._shared = shared;
    this._renderer = shared.renderer;
    this._scene = shared.scene;
    // The card's own canvas no longer holds a WebGL context — the shared
    // renderer draws off-DOM, and _renderFrame blits the result here every
    // frame, so this stays a plain 2D surface for that copy.
    this._ctx2d = canvas.getContext('2d');

    this._camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);

    this._texFront = new THREE.CanvasTexture(this._frontCanvas);
    this._texBack = new THREE.CanvasTexture(this._backCanvas);
    // The canvas is deliberately drawn larger than it's ever displayed
    // (TEXTURE_SCALE), so this texture is always being minified on screen —
    // Three's default is mipmapped trilinear filtering for that case, which
    // blurs through a lower-res mip chain and was working against the
    // oversampling rather than benefiting from it. Direct linear sampling
    // (no mips) uses the real, sharp, high-resolution texture as drawn.
    for (const tex of [this._texFront, this._texBack]) {
      tex.generateMipmaps = false;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
    }

    // Built once, directly at the card's own content aspect (its texture's
    // own W/H), rather than a generic unit square rescaled per frame — a
    // real rounded-rect stays circular in its corners this way, where a
    // non-uniform runtime scale on a unit square would have stretched them
    // into ellipses. this._baseScaleX/Y are stored for _renderFrame(),
    // replacing what used to be re-derived from live canvas pixels every
    // frame — the canvas is already CSS aspect-ratio-locked to this same
    // content aspect, so nothing is lost by fixing it at construction.
    const contentAspect = this._layout.W / this._layout.H;
    // The glass fills the element's width by a per-breakpoint constant and sits inside a margin that is
    // the same physical size on every card (see computeCardLayout), so the visible width is identical
    // everywhere, only the height grows, and the gap between two panels never depends on card height.
    // The camera sits 3.2 units away with a 32 degree field of view, so half the frame's height at
    // the card is 3.2 * tan(16 degrees) = 0.9175 and the frame is as wide as the element's aspect.
    const FRAME_HALF_H = 0.9175;
    const baseScaleY = (FRAME_HALF_H * this._layout.H) / this._layout.elH;
    const baseScaleX = (FRAME_HALF_H * this._layout.W) / this._layout.elH;
    this._baseScaleX = baseScaleX;
    this._baseScaleY = baseScaleY;

    const geomScale = this._layout.geomScale || 1;
    const cornerRadius = CARD_CORNER_RADIUS * geomScale;
    const cardDepth = CARD_DEPTH * geomScale;
    const shape = roundedRectShape(THREE, baseScaleX, baseScaleY, cornerRadius);
    // ExtrudeGeometry's default UV generator does NOT normalize cap UVs to
    // 0..1 the way a PlaneGeometry does — confirmed live (without this, the
    // texture only showed correctly in a small corner, clamped-to-edge
    // everywhere else). This maps cap UVs directly from each vertex's own
    // shape-space position, exactly like the flat plane's UVs used to be.
    const capUV = (vertices, index) => {
      const x = vertices[index * 3];
      const y = vertices[index * 3 + 1];
      return new THREE.Vector2((x + baseScaleX) / (2 * baseScaleX), (y + baseScaleY) / (2 * baseScaleY));
    };
    const uvGenerator = {
      generateTopUV: (geometry, vertices, a, b, c) => [capUV(vertices, a), capUV(vertices, b), capUV(vertices, c)],
      generateBottomUV: (geometry, vertices, a, b, c) => [capUV(vertices, a), capUV(vertices, b), capUV(vertices, c)],
      generateSideWallUV: () => [
        new THREE.Vector2(0, 0),
        new THREE.Vector2(1, 0),
        new THREE.Vector2(1, 1),
        new THREE.Vector2(0, 1),
      ],
    };
    this._geometry = new THREE.ExtrudeGeometry(shape, {
      depth: cardDepth,
      bevelEnabled: false,
      curveSegments: 12,
      UVGenerator: uvGenerator,
    });
    this._geometry.translate(0, 0, -cardDepth / 2); // extrudes 0..depth by default — recenter on z=0

    this._capMaterial = new THREE.ShaderMaterial({
      vertexShader: CAP_VERTEX_SHADER,
      fragmentShader: CAP_FRAGMENT_SHADER,
      uniforms: {
        uTexFront: { value: this._texFront },
        uTexBack: { value: this._texBack },
        uHalfSize: { value: new THREE.Vector2(baseScaleX, baseScaleY) },
        uRadius: { value: cornerRadius },
        uGeomScale: { value: geomScale },
        uTime: { value: 0 },
      },
      // Default FrontSide culling: only the cap actually facing the camera
      // rasterizes at all. The front/back branch above reads vLocalNormal
      // (fixed per physical cap, not the camera-facing one), so culling the
      // away-facing cap doesn't break which texture shows — it's what a
      // real opaque card would do anyway. DoubleSide was here earlier as a
      // workaround for a since-fixed bug (see the vLocalNormal comment
      // above); once the material went transparent for the glass look, it
      // became a real problem instead of a harmless no-op: the away-facing
      // cap's own low-alpha fragments were still being rasterized and
      // blended, so the OTHER side's text showed ghosted through the glass.
      transparent: true,
    });
    this._edgeMaterial = new THREE.ShaderMaterial({
      vertexShader: EDGE_VERTEX_SHADER,
      fragmentShader: EDGE_FRAGMENT_SHADER,
      uniforms: {
        uEdgeColor: { value: new THREE.Vector3(...CARD_EDGE_GOLD) },
        uTime: { value: 0 },
      },
    });

    // ExtrudeGeometry's default material grouping: index 0 = both flat caps
    // (front and back together), index 1 = the extruded side walls —
    // confirmed live (the reverse produced a solid edge-colored face).
    this._mesh = new THREE.Mesh(this._geometry, [this._capMaterial, this._edgeMaterial]);
    this._mesh.rotation.order = 'YXZ'; // yaw applied in world space, pitch inside the yawed frame —
    // matches the original hand-rolled translate * rotateY * rotateX order.
    // Not added to the scene here: the scene is shared with every other card
    // on the page, so membership is set fresh each frame in _renderFrame
    // instead of staying permanently attached.
  }

  _playUnfold() {
    if (this._reducedMotion) {
      this._unfoldProgress = 1;
      return;
    }
    this._gsap.to(this, {
      _unfoldProgress: 1,
      duration: 1.1,
      delay: this._unfoldDelay / 1000,
      ease: 'back.out(1.7)',
    });
  }

  _resize() {
    const canvas = this.refs.canvas;
    const rect = this.getBoundingClientRect();
    // Capping at 2x used to leave iPhone-class 3x screens rendering at a
    // lower backing-store resolution than the display itself, so the
    // browser had to upscale the final frame on top of an already-soft
    // texture — the actual cause of the blur, not the texture alone.
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (width === this._lastWidth && height === this._lastHeight) return;
    this._lastWidth = width;
    this._lastHeight = height;
    // The renderer is shared and sized to whichever card is rendering right
    // now (see _renderFrame) — this canvas is a plain 2D blit target, so its
    // own backing store is just sized to match, same as before.
    canvas.width = width;
    canvas.height = height;
    this._camera.aspect = width / height;
    this._camera.updateProjectionMatrix();
    if (this._reducedMotion) this._renderFrame();
  }

  /**
   * Enter/Space toggles the same _targetYaw the drag physics chase toward
   * each frame (see _tick) — the render loop's existing lerp animates the
   * turn smoothly, so keyboard and pointer users see the same motion, just
   * triggered differently. aria-pressed reflects which face is showing.
   */
  _onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    this._keyboardFlipped = !this._keyboardFlipped;
    this._targetYaw = this._keyboardFlipped ? Math.PI : 0;
    this.setAttribute('aria-pressed', String(this._keyboardFlipped));
  };

  _onPointerDown = (event) => {
    // A second finger touching down mid-drag would otherwise reset
    // _lastX/_lastY to the new contact point, snapping the card toward it —
    // a visible jump on touch devices. Ignore any pointer that isn't the
    // one already dragging.
    if (this._dragging) return;
    this._dragPointerId = event.pointerId;
    this._dragging = true;
    this._lastX = event.clientX;
    this._lastY = event.clientY;
    this._velYaw = 0;
    this._velPitch = 0;
    this.classList.add('is-dragging');
    // Keeps the drag locked to this pointer even if it leaves the card's
    // bounds mid-gesture, standard practice for pointer-driven drag.
    this.setPointerCapture?.(event.pointerId);

    // Grab-squish: a tiny, physical-feeling compression the instant a
    // finger or cursor presses the card, released just as fast — a real
    // GSAP tween on a plain number the render loop reads each frame, not a
    // hand-rolled lerp, since this is exactly the discrete choreographed
    // moment GSAP's easing curves are for. Works identically for touch and
    // mouse (pointerdown fires for both), so it isn't a desktop-only cue.
    this._gsap?.to(this, { _grabScale: 0.94, duration: 0.12, ease: 'power2.out', overwrite: true });
  };

  _onPointerMove = (event) => {
    if (!this._dragging || event.pointerId !== this._dragPointerId) return;
    const dx = event.clientX - this._lastX;
    const dy = event.clientY - this._lastY;
    this._lastX = event.clientX;
    this._lastY = event.clientY;
    // A full flip needs ~90° of yaw. On desktop there's plenty of room to
    // drag past that; on a phone a thumb runs out of screen well before
    // reaching the same pixel distance, so the flip used to stall halfway.
    // Mobile gets a real sensitivity boost, not just a cosmetic tweak.
    const sens = this._isDesktop ? 0.008 : 0.019;
    this._targetYaw += dx * sens;
    // Pitch (vertical tilt) is a completely different story on mobile: with
    // touch-action: pan-y, a scroll gesture that merely starts on the card
    // still leaks a few vertical pixels to JS before the browser commits it
    // to native scrolling — enough, at the old ±0.4 rad range, to visibly
    // tip the card on every accidental brush while scrolling past it. Kept
    // functional on desktop (a mouse drag is deliberate), but reduced to a
    // barely-there wobble on mobile — closer to haptic feedback than a
    // real tilt, so scrolling through the section never looks glitchy.
    const pitchSens = this._isDesktop ? sens * 0.75 : sens * 0.03;
    const pitchLimit = this._isDesktop ? 0.4 : 0.015;
    // Resistance rises as pitch nears its limit (real things slow before
    // stopping) rather than moving at full speed right up to a hard wall —
    // the clamp below stays only as a safety net for the residual velocity
    // added in _tick, not as the primary way the limit is felt.
    const headroom = 1 - (this._targetPitch / pitchLimit) ** 2;
    this._targetPitch = clamp(
      this._targetPitch - dy * pitchSens * Math.max(0, headroom),
      -pitchLimit,
      pitchLimit,
    );
    // Idle-sway momentum (see _tick) re-adds this every frame while it
    // decays, so its total carried rotation is velocity/(1-decay) — with a
    // 0.85 decay that's ~6.7x the raw value. A single fast flick (easy to
    // trigger by accident on a touchscreen, and mobile's own sensitivity is
    // already higher than desktop's) could inject enough velocity here to
    // spin the card through several full rotations before it settled —
    // "flips many times on its own". Capped well below that so a flick
    // still adds a little life without ever running away.
    this._velYaw = clamp(dx * sens, -0.35, 0.35);
    this._velPitch = clamp(-dy * pitchSens, -pitchLimit, pitchLimit);
  };

  _onPointerUp = (event) => {
    if (!this._dragging || (event && event.pointerId !== this._dragPointerId)) return;
    this._dragging = false;
    this._dragPointerId = null;
    this.classList.remove('is-dragging');
    this._gsap?.to(this, { _grabScale: 1, duration: 0.4, ease: 'back.out(2.4)', overwrite: true });
  };

  _startLoop() {
    if (this._reducedMotion || this._ticking) return;
    this._ticking = true;
    this._gsap.ticker.add(this._tick);
  }

  _stopLoop() {
    if (!this._ticking) return;
    this._ticking = false;
    this._gsap.ticker.remove(this._tick);
  }

  /* Continuous physics — drag-follow chase, inertia decay, idle sway — stay
     plain per-frame math rather than GSAP tweens, since there's no fixed
     end value to tween toward while a pointer is actively dragging; GSAP's
     ticker is only the frame source here; the actual chase/decay curve is
     hand-rolled, same formulas as the original raw-WebGL build. */
  _tick = () => {
    const now = performance.now();
    const t = (now - this._start) / 1000;
    if (!this._dragging) {
      // A ±3 degree sway made tall cards look crooked (a visible trapezoid), so cards now sit straight
      // on mobile and drift only a hair on desktop. Dragging and flipping are unaffected.
      const idleAmp = this._isDesktop ? 0.012 : 0;
      const idleYaw = Math.sin(t * 0.35) * idleAmp;
      const idlePitch = Math.sin(t * 0.27 + 1.3) * idleAmp * 0.6;
      this._targetYaw += (idleYaw - this._targetYaw) * 0.06 + this._velYaw;
      this._targetPitch += (idlePitch - this._targetPitch) * 0.06 + this._velPitch;
      this._velYaw *= 0.85;
      this._velPitch *= 0.85;
      // Safety net matching _onPointerMove's pitchLimit — this accumulation
      // isn't otherwise clamped, so a residual velPitch could still nudge
      // mobile's already-tiny pitch range past what "haptic, not a visible
      // tilt" actually means.
      const pitchLimit = this._isDesktop ? 0.4 : 0.015;
      this._targetPitch = clamp(this._targetPitch, -pitchLimit, pitchLimit);
    }
    this._yaw += (this._targetYaw - this._yaw) * 0.18;
    this._pitch += (this._targetPitch - this._pitch) * 0.18;

    const backFacing = Math.cos(this._yaw) < 0;
    if (backFacing !== this._wasBackFacing) {
      this._wasBackFacing = backFacing;
      this.dispatchEvent(
        new CustomEvent('fragrance:note-flip', {
          bubbles: true,
          detail: { color: this._tone.pulse, tier: this.dataset.tier },
        })
      );
    }

    this._renderFrame();
  };

  _renderFrame() {
    const shared = this._shared;
    if (!shared) return;
    const renderer = shared.renderer;
    const w = this._lastWidth;
    const h = this._lastHeight;
    if (w === 0 || h === 0) return;

    const eased = this._unfoldProgress;
    const unfoldYaw = (1 - eased) * 1.35;
    const unfoldScale = 0.18 + 0.82 * Math.max(0, Math.min(1, eased));
    const unfoldRise = (1 - eased) * 0.55;

    this._mesh.position.set(0, -unfoldRise, -3.2);
    this._mesh.rotation.set(this._pitch, this._yaw + unfoldYaw, 0);

    // A card scaled to exactly fill its frame gets its near edge pushed past
    // the canvas bounds by perspective as it rotates (the near side grows
    // toward the camera while the far side recedes). Rather than shrinking
    // the card at all times, the margin scales with how far it's actually
    // rotated right now — full size at rest, headroom only once it tilts.
    const totalYaw = this._yaw + unfoldYaw;
    const rotAmount = Math.min(1, Math.abs(Math.sin(totalYaw)) * 1.3 + Math.abs(Math.sin(this._pitch)) * 0.6);
    const dragSafeMargin = 1 - 0.22 * rotAmount;
    // The geometry is already built at the card's real aspect (baseScaleX/Y
    // baked into its vertices at construction), so this is a single uniform
    // factor now rather than two separately-derived X/Y scales.
    const uniformScale = unfoldScale * dragSafeMargin * this._grabScale;
    this._mesh.scale.set(uniformScale, uniformScale, uniformScale);

    const t = (performance.now() - this._start) / 1000;
    this._capMaterial.uniforms.uTime.value = t;
    this._edgeMaterial.uniforms.uTime.value = t;

    // Only one card renders at a time, so the shared renderer/canvas is
    // re-sized and the shared scene's membership re-set for whichever card
    // is rendering right now, immediately before its own render() call.
    if (renderer._plWidth !== w || renderer._plHeight !== h) {
      renderer.setSize(w, h, false);
      renderer._plWidth = w;
      renderer._plHeight = h;
    }
    shared.scene.clear();
    shared.scene.add(this._mesh);
    renderer.render(shared.scene, this._camera);

    // Copy the shared canvas's freshly-rendered pixels into this card's own
    // visible canvas — a plain 2D blit, not a second GL context. The source
    // is fully opaque-or-transparent per fragment (see the cap/edge
    // shaders' own alpha output) and the destination was just cleared, so
    // this is a lossless copy, not a re-blend.
    this._ctx2d.clearRect(0, 0, w, h);
    this._ctx2d.drawImage(shared.canvas, 0, 0, w, h, 0, 0, w, h);
  }
}

if (!customElements.get('fragrance-pyramid-card-component')) {
  customElements.define('fragrance-pyramid-card-component', FragrancePyramidCardComponent);
}
